import { useCallback, useId, useState, type ComponentProps } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SteadoMark } from '@/components/shell/SteadoMark';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { ApiError } from '@/lib/api/api-error';
import { useAuth } from '@/lib/auth/auth-context';

/**
 * Sign-in: the clip fills the page and the card floats on it.
 *
 * The copy says what the console is for in the words an operator would use,
 * and deliberately carries no API trivia. The imported design put
 * `JWT · ADMIN role`, `cursor pagination` and `{ success: true, data }` chips
 * here, which describe the transport to an audience that only wants to know
 * whether this is the right login page.
 *
 * The card is the one themed surface on the screen, which is why the theme
 * picker sits on it: the choice shows its effect where it is made. Everything
 * outside the card is over the video and stays light-on-dark in both themes.
 */
export function LoginScreen() {
  const { signIn } = useAuth();
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);

  // Derived from the element's own typings rather than React's deprecated
  // standalone `FormEvent` export.
  const onSubmit = useCallback<NonNullable<ComponentProps<'form'>['onSubmit']>>(
    (event) => {
      event.preventDefault();
      setError(null);
      setSubmitting(true);

      void signIn(email, password)
        .catch((cause: unknown) => {
          setError(
            cause instanceof ApiError ? cause.message : 'Something went wrong signing in. Please try again.',
          );
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    [signIn, email, password],
  );

  /*
   * The root clips with `overflow-clip`, not `overflow-hidden`.
   *
   * The drift-zoom video is scaled past the viewport on purpose, which makes a
   * `hidden` box a *scrollable* one — focusing anything on the card then
   * scrolls the whole page up to 10px sideways and leaves it there, which at
   * phone width puts the card's left edge off screen. `clip` clips the same
   * pixels without creating a scroll container, so there is nothing to scroll.
   */
  return (
    <div className="relative flex min-h-dvh flex-col overflow-clip bg-[#161514]">
      {/*
        The clip is the page's ground rather than a side panel, so it is graded
        rather than merely covered: `brightness` pulls it down before the scrim
        goes on, because a scrim alone flattens the plume into grey haze instead
        of dimming it. The card that sits on top needs the area behind it to be
        quiet at any crop, which the raw clip is not.

        The poster is the container's CSS background and the video sits on top,
        so a slow connection shows the still rather than a black rectangle, and
        `motion-reduce:hidden` leaves that still in place for anyone who has
        asked for less movement.
      */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[url('/login-poster.jpg')] bg-cover bg-center brightness-[0.45]"
      />
      {/*
        A sibling of the poster rather than a child of it. CSS filters compound
        down the tree, so nesting these multiplied the two `brightness` values
        and took the clip to roughly a quarter of its original luminance, which
        is black with a faint smudge in it. Side by side, the grade is applied
        once and both layers match.
      */}
      <video
        aria-hidden="true"
        className="absolute inset-0 size-full animate-driftzoom object-cover brightness-[0.45] saturate-[0.85] motion-reduce:hidden"
        src="/login-loop.mp4"
        poster="/login-poster.jpg"
        autoPlay
        loop
        muted
        playsInline
        tabIndex={-1}
      />

      {/* Deepest at the edges, so the card reads against a settled ground. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/70"
      />

      <header className="relative flex animate-rise items-center gap-2.5 p-6 sm:p-8">
        {/* Always over the video, so white rather than a theme token: the
            ground here is the clip, not the palette. */}
        <SteadoMark />
        <span className="text-[15px] font-semibold tracking-tight text-white">Steado</span>
        <span className="ml-1 hidden border-l border-white/25 pl-2.5 text-xs text-white/70 sm:inline">
          Admin console
        </span>
      </header>

      <main className="relative flex flex-1 flex-col items-center justify-center gap-8 px-4 pb-10 sm:px-8">
        {/* Hidden on short screens, where the card plus the keyboard already
            fill the viewport and this would push the fields off it. */}
        <p
          className="hidden max-w-[520px] animate-rise text-center text-[32px] leading-[1.12] font-semibold tracking-tight text-balance text-white sm:block"
          style={{ animationDelay: '60ms' }}
        >
          Curate the catalog. Inspect every account.
        </p>

        <div
          className="flex w-full max-w-[420px] animate-rise flex-col gap-7 rounded-xl border border-border bg-card p-7 shadow-2xl sm:p-9"
          style={{ animationDelay: '120ms' }}
        >
          <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">Sign in</h1>
              {/* Here rather than tucked in a corner of the page: the card is
                  the only themed surface on this screen, so it is the only
                  place the choice shows its effect as you make it. */}
              <ThemeToggle className="shrink-0" />
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Admin and support accounts only. Signing out or closing the browser ends the session.
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <Label htmlFor={emailId}>Email</Label>
              <Input
                id={emailId}
                type="email"
                name="email"
                autoComplete="username"
                required
                placeholder="ops@steado.ai"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor={passwordId}>Password</Label>
              <Input
                id={passwordId}
                type="password"
                name="password"
                autoComplete="current-password"
                required
                placeholder="••••••••"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
                {...(error === null ? {} : { 'aria-describedby': errorId })}
              />
            </div>

            {error === null ? null : (
              <Alert id={errorId} role="alert" tone="err">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>
      </main>

      <footer className="relative px-6 pb-8 text-center sm:px-8">
        <p
          className="mx-auto max-w-[520px] animate-rise text-[13px] leading-relaxed text-pretty text-white/60"
          style={{ animationDelay: '260ms' }}
        >
          Foods, exercises, generated plans, coach transcripts, queues and billing, all in one place.
        </p>
      </footer>
    </div>
  );
}
